import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../components/Icon';
import AdmissionForm from './AdmissionForm';
import MaskedPhone from '../components/MaskedPhone';
import IdPhotoViewer from '../components/IdPhotoViewer';
import StaffCard from '../components/StaffCard';
import API from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import './Enrollment.css';

const SECTION_KEYS = ['preschool','primary','secondary'];

const PAYMENT_OPTIONS = [
  { v: 'none', l: '—' },
  { v: 'depozit', l: 'Depozit' },
  { v: 'illik', l: 'İllik' },
  { v: 'depozit_40', l: 'Depozit+40%' },
];

const z = v => { const n = parseInt(v) || 0; return n === 0 ? '' : n; };

const ADM_STATUS_COLORS = {
  pending:   { color: '#d97706', bg: '#fef3c7' },
  reviewing: { color: '#0369a1', bg: '#e0f2fe' },
  accepted:  { color: '#059669', bg: '#d1fae5' },
  rejected:  { color: '#dc2626', bg: '#fee2e2' },
  // Sistem qurulmadan əvvəl qeydiyyatda olan şagirdlər — müraciət mərhələsi keçməyib
  enrolled:  { color: '#334155', bg: '#e2e8f0' },
  withdrawn: { color: '#64748b', bg: '#f1f5f9' },
};

export default function Enrollment() {
  const { t, lang } = useLanguage();
  const SECTIONS = [
    { v: 'preschool', l: t('enrollment.sectionPreschool') },
    { v: 'primary',   l: t('enrollment.sectionPrimary') },
    { v: 'secondary', l: t('enrollment.sectionSecondary') },
  ];
  const ADM_STATUS = {
    pending:   { l: t('enrollment.statusPending'),   ...ADM_STATUS_COLORS.pending },
    reviewing: { l: t('enrollment.statusReviewing'), ...ADM_STATUS_COLORS.reviewing },
    accepted:  { l: t('enrollment.statusAccepted'),  ...ADM_STATUS_COLORS.accepted },
    rejected:  { l: t('enrollment.statusRejected'),  ...ADM_STATUS_COLORS.rejected },
    enrolled:  { l: t('enrollment.statusEnrolled'),  ...ADM_STATUS_COLORS.enrolled },
    withdrawn: { l: t('enrollment.statusWithdrawn'), ...ADM_STATUS_COLORS.withdrawn },
  };
  const CAT_LABEL = {
    kindergarten: t('enrollment.catKindergarten'),
    lower:        t('enrollment.catLower'),
    upper:        t('enrollment.catUpper'),
  };
  const PREV_SCHOOL_LABEL = {
    unknown: '—',
    state:   t('admission.schoolState'),
    private: t('admission.schoolPrivate'),
    foreign: t('admission.schoolForeign'),
  };

  const CHILD_STATUS_LABEL = {
    normal:  '',
    embassy: t('admission.statusEmbassy'),
    staff:   t('enrollment.childStaff'),
    shahid:  t('enrollment.childMartyr'),
  };
  const PAYMENT_OPTIONS_T = [
    { v: 'none',        l: '—' },
    { v: 'depozit',     l: t('classes.paymentDeposit') },
    { v: 'illik',       l: t('classes.paymentAnnual') },
    { v: 'depozit_40',  l: t('classes.paymentDeposit40') },
  ];
  const locale = lang === 'en' ? 'en-US' : 'az-AZ';
  const [tab, setTab] = useState('stats');
  const [section, setSection] = useState('preschool');
  // Kəsilmiş xanaları tam görmək üçün sətir açılır
  /* Başlıq panelinin yığılması — seçim brauzerdə saxlanılır */
  const [hdrHidden, setHdrHidden] = useState(() => {
    try { return localStorage.getItem('ems_enr_hdr_hidden') === '1'; } catch { return false; }
  });
  const toggleHdr = () => setHdrHidden(v => {
    const next = !v;
    try { localStorage.setItem('ems_enr_hdr_hidden', next ? '1' : '0'); } catch {}
    return next;
  });
  const [expandedRow, setExpandedRow] = useState(null);
  /* Baxılan şəxsiyyət vəsiqəsi sənədləri — null olanda pəncərə bağlıdır */
  const [docView, setDocView] = useState(null);
  /* Bacı/qardaşa klikləyəndə işıqlanan sətir */
  const [flashSid, setFlashSid] = useState(null);
  /* Heyət övladının valideyninə klikləyəndə açılan kart */
  const [staffCardId, setStaffCardId] = useState(null);
  // Müraciətin (və ona bağlı şagirdin) düzəlişi bu səhifədə aparılır
  const [editAdm, setEditAdm] = useState(null);
  // Yeni müraciət də bu səhifədə açılır — Şöbələrə keçid yoxdur
  const [newAdm, setNewAdm] = useState(false);
  const [statsData, setStatsData] = useState(null);
  const [enrollList, setEnrollList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGrade, setSelectedGrade] = useState(null);

  // Sign modal
  const [signModal, setSignModal] = useState(null); // enrollment id
  const [signGrade, setSignGrade] = useState('');
  const [gradeOptions, setGradeOptions] = useState([]);

  // Admissions
  const [admissions, setAdmissions] = useState([]);
  // --- Qəbul müraciətləri: filtr və sıralama ---
  /* kind: '' (hamısı) | 'new' (qəbuldan gələn) | 'legacy' (köhnə şagirdlər) */
  const [admF, setAdmF] = useState({
    q: '', contract: '', parent: '', grade: '', year: '', status: '', kind: '',
  });
  const [admSort, setAdmSort] = useState({ key: 'created_at', dir: 'desc' });

  const setF = (k, v) => setAdmF(p => ({ ...p, [k]: v }));
  const clearF = () => setAdmF({ q: '', contract: '', parent: '', grade: '', year: '', status: '', kind: '' });
  const hasF = Object.values(admF).some(Boolean);

  const toggleSort = (key) => setAdmSort(p =>
    p.key === key ? { key, dir: p.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });

  // Sıralama üçün müqayisə dəyəri — sütuna görə fərqli sahələrdən yığılır
  const sortVal = (a, key) => {
    switch (key) {
      case 'request_number': return a.request_number || '';
      case 'created_at':     return a.created_at || '';
      case 'student':        return `${a.student_last_name || ''} ${a.student_first_name || ''}`.trim();
      case 'father':         return a.student_father_name || '';
      case 'category':       return a.category || '';
      case 'grade':          return a.target_grade || '';
      case 'parent':         return `${a.parent1_first_name || ''} ${a.parent1_last_name || ''}`.trim();
      case 'phone':          return a.parent1_phone || '';
      case 'email':          return a.parent1_email || '';
      case 'contract':       return a.contract_number || '';
      case 'sibling':        return a.sibling_name || '';
      case 'prevschool':     return a.previous_school_type || '';
      case 'status':         return a.status || '';
      case 'child_status':   return a.child_status || '';
      case 'year':           return a.academic_year || '';
      default:               return '';
    }
  };

  /* Köhnə şagird sətirlərinin id-si yoxdur — açılan sətri ayırd etmək üçün açar */
  const rowKey = (a) => a.id || 'L' + a.legacy_enrollment_id;

  /* Sistemdəki şagirdə bağlanmayan bacı/qardaş adları.
     sibling_name-də həm bağlı, həm bağlanmayan adlar olur; bağlı olanlar
     ayrıca nişanla göstərildiyi üçün burada təkrarlanmır. */
  const unlinkedSiblings = (a) => {
    const linked = new Set((a.siblings || []).map(s => String(s.name || '').trim().toLowerCase()));
    return String(a.sibling_name || '')
      .split(',').map(x => x.trim()).filter(Boolean)
      .filter(nm => !linked.has(nm.toLowerCase()));
  };

  /* Sətrin arxasındaki şagird qeydi: köhnə sətirdə birbaşa, qəbuldan
     gələndə isə qeydiyyat vasitəsilə bağlanan şagird. Bacı/qardaşa
     klikləyəndə onun sətrini məhz bununla tapırıq. */
  const studentIdOf = (a) => a.row_kind === 'legacy'
    ? a.legacy_student_id
    : a.enrolled_student_id;

  const jumpToStudent = (sid) => {
    if (!sid) return;
    const inView = admView.some(x => studentIdOf(x) === sid);
    // Axtarılan şagird filtrdən kənarda qalıbsa, filtrlər təmizlənir
    if (!inView) {
      if (!admissions.some(x => studentIdOf(x) === sid)) {
        alert(t('enrollment.siblingNotInList'));
        return;
      }
      clearF();
    }
    setFlashSid(sid);
    // Filtr təmizlənəndə sətir yenidən render olunur — ona görə gözləyirik
    setTimeout(() => {
      const el = document.querySelector('tr[data-sid="' + sid + '"]');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, inView ? 0 : 120);
    setTimeout(() => setFlashSid(null), 2600);
  };

  /* Müraciətə bağlı şəxsiyyət vəsiqəsi şəkilləri — baxış pəncərəsi üçün siyahı.
     Fayl adları DB-də saxlanılır, özləri serverdə backend/uploads/admission/ içindədir. */
  const admDocs = (a) => {
    const out = [];
    const who = (f, l) => ((f || '') + ' ' + (l || '')).trim();
    if (a.parent1_id_photo) out.push({ name: a.parent1_id_photo,
      label: t('enrollment.admDocP1') + (who(a.parent1_first_name, a.parent1_last_name) ? ' · ' + who(a.parent1_first_name, a.parent1_last_name) : ''),
      download: (a.request_number || 'sened') + '-valideyn1' });
    if (a.parent2_id_photo) out.push({ name: a.parent2_id_photo,
      label: t('enrollment.admDocP2') + (who(a.parent2_first_name, a.parent2_last_name) ? ' · ' + who(a.parent2_first_name, a.parent2_last_name) : ''),
      download: (a.request_number || 'sened') + '-valideyn2' });
    return out;
  };

  // Sıralanan başlıq: ox işarəsi hazırkı sıranı göstərir
  const SortTh = ({ k, children, className, style, full }) => (
    <th className={(className || '') + ' th-sort' + (admSort.key === k ? ' active' : '')}
        style={style} onClick={() => toggleSort(k)}
        title={(full ? full + ' — ' : '') + t('enrollment.sortHint')}>
      <span className="th-sort-in">
        {children}
        <span className={'th-arrow' + (admSort.key === k ? ' on' : '')}>
          {admSort.key === k ? (admSort.dir === 'asc' ? '▲' : '▼') : '▾'}
        </span>
      </span>
    </th>
  );

  const admView = React.useMemo(() => {
    const norm = (x) => String(x || '').toLowerCase();
    let out = (admissions || []).filter(a => {
      if (admF.q) {
        const hay = norm(`${a.student_first_name} ${a.student_last_name} ${a.student_father_name}`);
        if (!hay.includes(norm(admF.q))) return false;
      }
      if (admF.contract && !norm(a.contract_number).includes(norm(admF.contract))) return false;
      if (admF.parent) {
        const hay = norm(`${a.parent1_first_name} ${a.parent1_last_name}`);
        if (!hay.includes(norm(admF.parent))) return false;
      }
      if (admF.grade  && !norm(a.target_grade).includes(norm(admF.grade))) return false;
      if (admF.year   && norm(a.academic_year) !== norm(admF.year)) return false;
      if (admF.kind   && (a.row_kind || 'new') !== admF.kind) return false;
      /* Ayrılmış şagirdlər yalnız "Ayrılıb" filtri ilə görünür — standart
         siyahıdaki say kompleksdə faktiki oxuyan şagird sayını göstərsin. */
      if (admF.status === 'withdrawn') {
        if (a.enrollment_status !== 'out') return false;
      } else {
        if (a.enrollment_status === 'out') return false;
        if (admF.status && a.status !== admF.status) return false;
      }
      return true;
    });
    const { key, dir } = admSort;
    out = [...out].sort((x, y) => {
      const r = String(sortVal(x, key)).localeCompare(String(sortVal(y, key)), 'az', { numeric: true });
      return dir === 'asc' ? r : -r;
    });
    return out;
  }, [admissions, admF, admSort]);   // eslint-disable-line react-hooks/exhaustive-deps

  /* Siyahının altındaki saylar: aktiv oxuyanlar, ayrılanlar və
     qəbul edilib hələ qeydiyyata düşməyənlər */
  const admCounts = React.useMemo(() => {
    const a = admissions || [];
    return {
      out:     a.filter(x => x.enrollment_status === 'out').length,
      active:  a.filter(x => x.enrollment_status === 'active').length,
      waiting: a.filter(x => !x.enrollment_status).length,
    };
  }, [admissions]);

  // Filtrdəki "tədris ili" seçimi mövcud məlumatdan yığılır
  const admYears = React.useMemo(() => {
    const set = new Set((admissions || []).map(a => a.academic_year).filter(Boolean));
    return [...set].sort().reverse();
  }, [admissions]);

  const [admLoading, setAdmLoading] = useState(false);

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const r = await API.get('/enrollment/stats', { params: { section } });
      setStatsData(r.data);
    } catch (e) {} finally { setLoading(false); }
  }, [section]);

  const loadEnrollList = async (grade) => {
    try {
      // If grade is '_all', load all for section; otherwise filter by grade
      const params = { section };
      if (grade && grade !== '_all') params.grade = grade;
      const r = await API.get('/enrollment', { params });
      setEnrollList(r.data);
    } catch (e) {}
  };

  const loadAdmissions = async () => {
    setAdmLoading(true);
    try {
      // include=all: qəbuldan gələnlər + köhnə qeydiyyatdakı şagirdlər
      const r = await API.get('/admission', { params: { include: 'all' } });
      setAdmissions(r.data);
    } catch (e) {} finally { setAdmLoading(false); }
  };

  /* Redaktə həmişə qəbul qeydi üzərindən gedir. Köhnə şagirdin belə qeydi
     olmadığı üçün ilk redaktədə ona qeyd açılır, sonra forma həmin qeydi açır. */
  const openEdit = async (a) => {
    if ((a.row_kind || 'new') !== 'legacy') { setEditAdm({ ...a }); return; }
    try {
      const r = await API.post('/admission/from-enrollment/' + a.legacy_enrollment_id);
      const full = await API.get('/admission/' + r.data.id);
      setEditAdm(full.data);
      loadAdmissions();
    } catch (e) {
      alert(e.response?.data?.message || t('enrollment.legacyEditFailed'));
    }
  };

  useEffect(() => { if (tab === 'stats') loadStats(); }, [loadStats, tab]);
  useEffect(() => { if (tab === 'admissions') loadAdmissions(); }, [tab]);

  const openGrade = (grade) => {
    setSelectedGrade(grade);
    setTab('list');
    loadEnrollList(grade);
  };

  const updateField = async (id, field, value) => {
    try {
      await API.put('/enrollment/' + id, { [field]: value });
      loadEnrollList(selectedGrade);
      loadStats();
    } catch (e) { alert(e.response?.data?.error || 'Xəta'); }
  };

  const openSignModal = async (enrollment) => {
    setSignModal(enrollment);
    setSignGrade('');
    try {
      // Get all grades for this section with their current counts
      const [statsRes, gradesRes] = await Promise.all([
        API.get('/enrollment/stats', { params: { section } }),
        API.get('/enrollment/grades', { params: { section } }),
      ]);
      const gradeStats = statsRes.data.grades || [];
      const enrollGrade = enrollment.grade || '';
      // Filter: show only grades that start with the student's base grade number
      // e.g. grade "10" → show "10 A", "10 R1", "10 i1" etc.
      const baseNum = enrollGrade.replace(/[^0-9\-]/g, '').trim();
      const opts = gradeStats
        .filter(g => {
          if (!baseNum) return true; // show all if no number
          const gNum = g.grade.replace(/[^0-9\-]/g, '').trim();
          return gNum === baseNum || g.grade.toLowerCase().startsWith(baseNum.toLowerCase());
        })
        .map(g => ({
          grade: g.grade,
          capacity: g.capacity,
          current: parseInt(g.total_active) || 0,
          available: g.capacity - (parseInt(g.total_active) || 0),
          isFull: (parseInt(g.total_active) || 0) >= g.capacity,
        }));
      setGradeOptions(opts);
    } catch (e) {}
  };

  const confirmSign = async () => {
    if (!signGrade) return alert('Sinif seçin');
    try {
      // Update grade to specific index + sign
      await API.put('/enrollment/' + signModal.id, { grade: signGrade });
      await API.put('/enrollment/' + signModal.id + '/sign');
      setSignModal(null);
      loadEnrollList(selectedGrade);
      loadStats();
    } catch (e) { alert(e.response?.data?.error || 'Xəta'); }
  };

  const updatePayment = async (id, val) => {
    try { await API.put('/enrollment/' + id + '/payment', { payment_status: val }); loadEnrollList(selectedGrade); loadStats(); } catch (e) {}
  };

  const updateAdmStatus = async (id, status, note) => {
    try {
      await API.put('/admission/' + id + '/status', { status, note });
      loadAdmissions();
      loadStats();
    } catch (e) { alert(e.response?.data?.error || 'Xəta'); }
  };

  const tot = statsData?.totals || {};
  const pendingAdm = admissions.filter(a => a.status === 'pending').length;
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  const handleExcelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true); setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await API.post('/enrollment/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadResult(res.data);
      loadStats();
    } catch (err) {
      setUploadResult({ imported: 0, errors: [err.response?.data?.error || 'Xəta baş verdi'] });
    } finally { setUploading(false); e.target.value = ''; }
  };

  return (
    <div className="enr-page">
      {/* Başlıq, sekmələr və cədvəl bir panelin içindədir — ayrı-ayrı
          qutular səhifəni parçalanmış göstərirdi. */}
      <div className={'enr-panel' + (hdrHidden ? ' hdr-hidden' : '')}>
      {/* Başlıq paneli yığıla bilər — noutbukda cədvəl üçün yer açır */}
      <button className="enr-hdr-toggle" onClick={toggleHdr}
        title={hdrHidden ? t('enrollment.showHeader') : t('enrollment.hideHeader')}>
        {/* Üç qatlı ox — yığmaq/açmaq işarəsi */}
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
          strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
          {hdrHidden ? (
            <>
              <polyline points="7 5 12 10 17 5" />
              <polyline points="7 10 12 15 17 10" />
              <polyline points="7 15 12 20 17 15" />
            </>
          ) : (
            <>
              <polyline points="7 10 12 5 17 10" />
              <polyline points="7 15 12 10 17 15" />
              <polyline points="7 20 12 15 17 20" />
            </>
          )}
        </svg>
      </button>
      <div className="enr-header">
        <div>
          <h2>{t('enrollment.title')}</h2>
          <span style={{fontSize:'0.82rem',color:'#6b7280'}}>{t('enrollment.academicYear')}</span>
        </div>
        {/* Əməliyyatlar: bir əsas düymə, qalanı tabe.
            Şablon və Excel Yüklə eyni iş axınının hissəsidir (şablonu endir →
            doldur → yüklə), ona görə yan-yana qruplaşdırılıb. */}
        <div className="enr-actions">
          <button type="button" className="enr-act ghost" onClick={async () => {
            try {
              const res = await API.get('/enrollment/template', { responseType: 'blob' });
              const url = window.URL.createObjectURL(new Blob([res.data]));
              const a = document.createElement('a'); a.href = url; a.download = 'enrollment_template.xlsx'; a.click();
              window.URL.revokeObjectURL(url);
            } catch(e) { alert(t('common.error')); }
          }}>
            <Icon name="download" size={15} />
            {t('enrollment.btnTemplate')}
          </button>

          <label className={'enr-act outline' + (uploading ? ' is-busy' : '')}>
            <Icon name="upload" size={15} />
            {uploading ? t('enrollment.btnUploading') : t('enrollment.btnUpload')}
            <input type="file" accept=".xlsx,.xls" onChange={handleExcelUpload}
              style={{display:'none'}} disabled={uploading} />
          </label>

        </div>
      </div>

      {/* Upload result */}
      {uploadResult && (
        <div style={{
          padding:'12px 16px',borderRadius:10,marginBottom:14,
          background:uploadResult.errors?.length ? '#fef2f2' : '#d1fae5',
          border:'1px solid '+(uploadResult.errors?.length ? '#fca5a5' : '#6ee7b7'),
          display:'flex',justifyContent:'space-between',alignItems:'center'
        }}>
          <div>
            <b style={{color:uploadResult.imported > 0 ? '#059669' : '#dc2626'}}>
              {uploadResult.imported} {t('enrollment.importedSuffix')}
            </b>
            {uploadResult.errors?.length > 0 && (
              <div style={{fontSize:'0.78rem',color:'#dc2626',marginTop:4}}>
                {uploadResult.errors.slice(0,5).map((err,i) => <div key={i}>• {err}</div>)}
                {uploadResult.errors.length > 5 && <div>... {uploadResult.errors.length-5} {t('enrollment.moreErrors')}</div>}
              </div>
            )}
          </div>
          <button onClick={() => setUploadResult(null)} style={{background:'none',border:'none',fontSize:'1.1rem',cursor:'pointer',color:'#9ca3af'}}><Icon name="close" size={16} /></button>
        </div>
      )}

      {/* Sekmələr və həmin bölmənin əsas əməliyyatı eyni sətirdə —
          düymə üçün ayrıca sətir tutmağa dəyməz. */}
      <div className="enr-tabrow">
        <div className="enr-main-tabs">
          <button className={'enr-main-tab' + (tab === 'stats' || tab === 'list' ? ' active' : '')} onClick={() => { setTab('stats'); setSelectedGrade(null); }}>
            {t('enrollment.sectionTable')}
          </button>
          <button className={'enr-main-tab' + (tab === 'admissions' ? ' active' : '')} onClick={() => setTab('admissions')}>
            {t('enrollment.sectionRequests')}
            {pendingAdm > 0 && <span className="enr-pending-badge">{pendingAdm}</span>}
          </button>
        </div>

        {tab === 'admissions' && (
          <button type="button" className="enr-act primary"
            onClick={() => setNewAdm(true)}>
            <Icon name="userPlus" size={15} />
            {t('enrollment.btnNewAdmission')}
          </button>
        )}
      </div>

      {/* STATS TABLE (Excel-like view) */}
      {tab === 'stats' && (
        <>
          <div className="enr-tabs">
            {SECTIONS.map(s => {
              // Sinfi təyin olunmayanların sayı bölmə üzrə — istifadəçi
              // sekmələri bir-bir açıb axtarmasın deyə nişan göstərilir
              const n = statsData?.pending_by_section?.[s.v] || 0;
              return (
                <button
                  key={s.v}
                  className={'enr-tab' + (section === s.v ? ' active' : '')}
                  onClick={() => setSection(s.v)}>
                  {s.l}
                  {n > 0 && (
                    <span className="enr-tab-badge"
                      title={n + ' şagirdin sinfi təyin olunmayıb'}>{n}</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Pending - unassigned students */}
          {!loading && (statsData?.pending || []).length > 0 && (
            <div className="enr-pending-bar" onClick={() => { setSelectedGrade('_all'); setTab('list'); loadEnrollList('_all'); }}>
              <Icon name="warning" size={17} />
              <span><b>{(statsData.pending || []).reduce((s, p) => s + parseInt(p.count), 0)}</b> {t('enrollment.pendingBarText')}</span>
              <span className="enr-pending-action">{t('enrollment.pendingBarOpen')}</span>
            </div>
          )}

          {loading ? <div className="enr-loading">{t('common.loading')}</div> : (
            <div className="enr-table-wrap">
              <table className="enr-stats-table">
                <thead>
                  <tr>
                    <th rowSpan={2} className="th-grade">{t('enrollment.colGrade')}</th>
                    <th colSpan={3} className="th-group continuing">{t('enrollment.grpContinuing')}</th>
                    <th colSpan={3} className="th-group new-std">{t('enrollment.grpNew')}</th>
                    <th colSpan={5} className="th-group total">{t('enrollment.grpTotal')}</th>
                    <th rowSpan={2} className="th-avail">{t('enrollment.colAvailable')}</th>
                  </tr>
                  <tr>
                    <th className="th-sub continuing">{t('enrollment.subExpected')}</th>
                    <th className="th-sub continuing">{t('enrollment.subWithdrawn')}</th>
                    <th className="th-sub continuing">{t('enrollment.subCurrent')}</th>
                    <th className="th-sub new-std">{t('enrollment.grpNew')}</th>
                    <th className="th-sub new-std">{t('enrollment.subWithdrawn')}</th>
                    <th className="th-sub new-std">{t('enrollment.subCurrent')}</th>
                    <th className="th-sub total">{t('enrollment.subOverall')}</th>
                    <th className="th-sub total">{t('enrollment.subDeposit')}</th>
                    <th className="th-sub total">{t('enrollment.subSignedPay')}</th>
                    <th className="th-sub total">{t('enrollment.subStaff')}</th>
                    <th className="th-sub total">{t('enrollment.subMartyr')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(statsData?.grades || []).map(g => {
                    const contCurrent = Number(g.continuing_active) || 0;
                    const contExpected = contCurrent + (Number(g.continuing_withdrawal) || 0);
                    const newCurrent = Number(g.new_active) || 0;
                    return (
                      <tr key={g.section + '|' + g.grade}
                        className={'stats-row' + (g.no_capacity ? ' no-cap' : '')
                          + (g.needs_letter ? ' needs-letter' : '')}
                        onClick={() => openGrade(g.grade)}>
                        <td className="td-grade">
                          {g.grade}
                          {/* Hərfi təyin olunmayan şagirdlər: qrup seçilməyib */}
                          {g.needs_letter && (
                            <span className="td-noletter" title={t('enrollment.needsLetterHint')}>
                              {t('enrollment.needsLetterTag')}
                            </span>
                          )}
                          {/* Tutumu təyin olunmayan sinif — şagirdlər sayılır,
                              amma boş yer hesablanmır */}
                          {g.no_capacity && (
                            <span className="td-nocap" title={t('enrollment.noCapacityHint')}>!</span>
                          )}
                        </td>
                        <td className="td-cont">{z(contExpected)}</td>
                        <td className="td-cont">{z(g.continuing_withdrawal)}</td>
                        <td className="td-cont hi">{z(contCurrent)}</td>
                        <td className="td-new">{z(+g.new_active + +g.new_withdrawal)}</td>
                        <td className="td-new">{z(g.new_withdrawal)}</td>
                        <td className="td-new hi">{z(newCurrent)}</td>
                        <td className="td-total">{z(g.total_active)}</td>
                        <td className="td-total">{z(g.deposit_count)}</td>
                        <td className="td-total">{z(g.signed_not_paid)}</td>
                        <td className="td-total">{z(g.staff_count)}</td>
                        <td className="td-total">{z(g.shahid_count)}</td>
                        {g.no_capacity ? (
                          <td className="td-avail td-nocap-cell">—</td>
                        ) : (
                          <td className={'td-avail' + (g.available <= 0 ? ' full' : g.available <= 3 ? ' low' : '')}>{z(g.available) || (g.available < 0 ? g.available : '')}</td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  {(() => {
                    const grades = statsData?.grades || [];
                    const sum = key => grades.reduce((s, g) => s + (parseInt(g[key]) || 0), 0);
                    const ca = sum('continuing_active'), cw = sum('continuing_withdrawal');
                    const na = sum('new_active'), nw = sum('new_withdrawal');
                    return (
                      <tr className="stats-total-row">
                        <td className="td-grade"><b>{t('enrollment.rowTotal')}</b></td>
                        <td className="td-cont"><b>{z(ca + cw)}</b></td>
                        <td className="td-cont"><b>{z(cw)}</b></td>
                        <td className="td-cont hi"><b>{z(ca)}</b></td>
                        <td className="td-new"><b>{z(na + nw)}</b></td>
                        <td className="td-new"><b>{z(nw)}</b></td>
                        <td className="td-new hi"><b>{z(na)}</b></td>
                        <td className="td-total"><b>{z(sum('total_active'))}</b></td>
                        <td className="td-total"><b>{z(sum('deposit_count'))}</b></td>
                        <td className="td-total"><b>{z(sum('signed_not_paid'))}</b></td>
                        <td className="td-total"><b>{z(sum('staff_count'))}</b></td>
                        <td className="td-total"><b>{z(sum('shahid_count'))}</b></td>
                        <td className="td-avail"></td>
                      </tr>
                    );
                  })()}
                </tfoot>
              </table>
            </div>
          )}
        </>
      )}

      {/* GRADE DETAIL LIST */}
      {tab === 'list' && selectedGrade && (
        <>
          <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:14}}>
            <button className="enr-back-btn" onClick={() => { setTab('stats'); setSelectedGrade(null); }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
            </button>
            <h3 style={{margin:0,fontSize:'1.1rem',color:'#111827'}}>{selectedGrade}</h3>
            <span style={{fontSize:'0.82rem',color:'#9ca3af'}}>{enrollList.length} {t('enrollment.studentSuffix')}</span>
          </div>
          <div className="enr-table-wrap">
            <table className="enr-table">
              <thead>
                <tr>
                  <th style={{width:36}}>{t('enrollment.admColNum')}</th>
                  <th style={{width:70}}>{t('enrollment.admColStatus')}</th>
                  <th>{t('enrollment.detailColLastName')}</th>
                  <th>{t('enrollment.detailColFirstName')}</th>
                  <th>{t('enrollment.detailColFatherName')}</th>
                  <th style={{width:65,textAlign:'center'}}>{t('enrollment.detailColType')}</th>
                  <th style={{width:80,textAlign:'center'}}>{t('enrollment.detailColSign')}</th>
                  <th style={{width:105,textAlign:'center'}}>{t('enrollment.detailColSignDate')}</th>
                  <th style={{width:115,textAlign:'center'}}>{t('enrollment.detailColPayment')}</th>
                  <th style={{width:70,textAlign:'center'}}>{t('enrollment.detailColCat')}</th>
                </tr>
              </thead>
              <tbody>
                {enrollList.length === 0 && (
                  <tr><td colSpan={10} style={{textAlign:'center',padding:32,color:'#9ca3af'}}>{t('enrollment.detailEmpty')}</td></tr>
                )}
                {enrollList.map((e, i) => {
                  const isOut = e.enrollment_status === 'out';
                  const lastName = e.student_last_name || (e.student_name || '').split(' ').slice(-1)[0] || '—';
                  const firstName = e.student_first_name || (e.student_name || '').split(' ')[0] || '—';
                  const fatherName = e.student_father_name || '—';
                  return (
                    <tr key={e.id} className={isOut ? 'row-out' : ''}>
                      <td className="enr-num">{i + 1}</td>
                      <td>
                        <select className={'enr-status-sel' + (isOut ? ' out' : ' active')}
                          value={e.enrollment_status}
                          onChange={ev => {
                            if (ev.target.value === 'out' && !window.confirm(firstName + ' ' + lastName + ' — ' + t('enrollment.confirmOut'))) return;
                            updateField(e.id, 'enrollment_status', ev.target.value);
                          }}>
                          <option value="active">{t('enrollment.statusActive')}</option>
                          <option value="out">{t('enrollment.statusOut')}</option>
                        </select>
                      </td>
                      <td className="enr-name">{lastName}</td>
                      <td className="enr-name">{firstName}</td>
                      <td>{fatherName}</td>
                      <td style={{textAlign:'center'}}>
                        <span className={'enr-type-badge ' + (e.student_type || 'new')}>
                          {e.student_type === 'continuing' ? t('enrollment.typeContinuing') : t('enrollment.typeNew')}
                        </span>
                      </td>
                      <td style={{textAlign:'center'}}>
                        {e.signing_status === 'signed' ? (
                          <span className="enr-badge green">{t('enrollment.paySigned')}</span>
                        ) : isOut ? (
                          <span className="enr-badge gray">{t('enrollment.statusOut')}</span>
                        ) : (
                          <button className="enr-sign-btn" onClick={() => openSignModal(e)}>{t('enrollment.btnSign')}</button>
                        )}
                      </td>
                      <td style={{textAlign:'center'}} className="enr-date">
                        {e.signing_date ? new Date(e.signing_date).toLocaleDateString(locale) : '—'}
                      </td>
                      <td style={{textAlign:'center'}}>
                        <select className="enr-pay-sel" value={e.payment_status}
                          onChange={ev => updatePayment(e.id, ev.target.value)} disabled={isOut}>
                          {PAYMENT_OPTIONS_T.map(p => <option key={p.v} value={p.v}>{p.l}</option>)}
                        </select>
                      </td>
                      <td style={{textAlign:'center'}}>
                        {e.child_status && e.child_status !== 'normal' && (
                          <span className={'enr-badge ' + (e.child_status === 'shahid' ? 'red' : 'purple')}>
                            {CHILD_STATUS_LABEL[e.child_status]}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ADMISSIONS TAB */}
      {tab === 'admissions' && (
        <>
          <div className="adm-filters">
            {/* Şagird növü: köhnə qeydiyyatdakılar və qəbuldan gələnlər bir siyahıdadır */}
            <select className="adm-f sm" value={admF.kind} onChange={e => setF('kind', e.target.value)}>
              <option value="">{t('enrollment.fKindAll')}</option>
              <option value="new">{t('enrollment.fKindNew')}</option>
              <option value="legacy">{t('enrollment.fKindLegacy')}</option>
            </select>
            <input className="adm-f" value={admF.q} onChange={e => setF('q', e.target.value)}
              placeholder={t('enrollment.fAdmStudent')} />
            <input className="adm-f" value={admF.parent} onChange={e => setF('parent', e.target.value)}
              placeholder={t('enrollment.fAdmParent')} />
            <input className="adm-f sm" value={admF.contract} onChange={e => setF('contract', e.target.value)}
              placeholder={t('admission.fieldContractNo')} />
            <input className="adm-f sm" value={admF.grade} onChange={e => setF('grade', e.target.value)}
              placeholder={t('enrollment.admColGradeAge')} />
            <select className="adm-f sm" value={admF.year} onChange={e => setF('year', e.target.value)}>
              <option value="">{t('admission.fieldAcademicYear')}</option>
              {admYears.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <select className="adm-f sm" value={admF.status} onChange={e => setF('status', e.target.value)}>
              <option value="">{t('enrollment.admColStatus')}</option>
              {Object.entries(ADM_STATUS).map(([k, v]) => <option key={k} value={k}>{v.l}</option>)}
            </select>
            {hasF && (
              <button type="button" className="adm-f-clear" onClick={clearF}>
                <Icon name="close" size={13} /> {t('common.clear')}
              </button>
            )}
            {/* Say kompleksdə faktiki oxuyan şagirdi göstərir;
                ayrılanlar ayrıca yazılır və bir kliklə açılır */}
            <span className="adm-f-count">
              <b>{admView.length}</b> {t('enrollment.countStudents')}
              {admCounts.out > 0 && !admF.status && (
                <button type="button" className="adm-f-out"
                  title={t('enrollment.showWithdrawnHint')}
                  onClick={() => setF('status', 'withdrawn')}>
                  +{admCounts.out} {t('enrollment.statusWithdrawn').toLowerCase()}
                </button>
              )}
            </span>
          </div>

          {admLoading ? <div className="enr-loading">{t('common.loading')}</div> : (
            <div className="enr-table-wrap adm-scroll">
              <table className="enr-table adm-table">
                <thead>
                  <tr>
                    <SortTh k="request_number">{t('enrollment.admColNum')}</SortTh>
                    <SortTh k="created_at">{t('enrollment.admColDate')}</SortTh>
                    <SortTh k="student">{t('enrollment.admColStudent')}</SortTh>
                    <SortTh k="father">{t('enrollment.admColFatherName')}</SortTh>
                    <SortTh k="category">{t('enrollment.admColCategory')}</SortTh>
                    <SortTh k="grade">{t('enrollment.admColGradeAge')}</SortTh>
                    <SortTh k="parent" className="th-wrap">{t('enrollment.admColParent')}</SortTh>
                    <SortTh k="phone">{t('enrollment.admColPhone')}</SortTh>
                    <SortTh k="email" className="th-wrap">{t('enrollment.admColParentEmail')}</SortTh>
                    <SortTh k="contract" full={t('enrollment.admColContractFull')}>{t('enrollment.admColContract')}</SortTh>
                    <SortTh k="sibling" full={t('enrollment.admColSiblingFull')}>{t('enrollment.admColSibling')}</SortTh>
                    <SortTh k="prevschool" className="th-wrap">{t('admission.sectionPrevSchool')}</SortTh>
                    <th style={{width:86, textAlign:'center'}}>{t('enrollment.admColDoc')}</th>
                    <SortTh k="status">{t('enrollment.admColStatus')}</SortTh>
                    <SortTh k="child_status" full={t('enrollment.admColCatFull')}>{t('enrollment.admColCat')}</SortTh>
                    <th style={{width:130, textAlign:'center'}}>{t('enrollment.admColActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {admView.length === 0 && (
                    <tr><td colSpan={16} style={{textAlign:'center',padding:48,color:'#94a3b8',fontWeight:500}}>{t('enrollment.admEmpty')}</td></tr>
                  )}
                  {admView.map(a => {
                    const st = ADM_STATUS[a.status] || ADM_STATUS.pending;
                    return (
                      <tr key={a.id || 'L' + a.legacy_enrollment_id}
                        data-sid={studentIdOf(a) || undefined}
                        className={(expandedRow === rowKey(a) ? 'row-expanded' : '')
                          + (a.row_kind === 'legacy' ? ' row-legacy' : '')
                          + (flashSid && studentIdOf(a) === flashSid ? ' row-flash' : '')}
                        onClick={() => setExpandedRow(expandedRow === rowKey(a) ? null : rowKey(a))}
                        style={{ cursor: 'pointer' }}
                        title="Tam mətni görmək üçün klikləyin">
                        <td className="enr-num">
                          {a.request_number || <span className="adm-muted">—</span>}
                        </td>
                        <td className="enr-date">{new Date(a.created_at).toLocaleDateString(locale)}</td>
                        <td className="enr-name">{a.student_last_name} {a.student_first_name}</td>
                        <td>{a.student_father_name}</td>
                        <td>{CAT_LABEL[a.category] || a.category}</td>
                        <td className="enr-grade" style={{textAlign:'center'}}>{a.target_grade || '—'}</td>
                        <td>{a.parent1_first_name} {a.parent1_last_name}</td>
                        <td className="adm-phone">
                          <MaskedPhone value={a.parent1_phone} />
                        </td>
                        <td className="adm-email" title={a.parent1_email || ''}>
                          {a.parent1_email
                            ? <a href={'mailto:' + a.parent1_email} onClick={e => e.stopPropagation()}>{a.parent1_email}</a>
                            : <span className="adm-muted">—</span>}
                        </td>
                        <td className="adm-contract">{a.contract_number || <span className="adm-muted">—</span>}</td>
                        <td className="adm-sibling" title={a.sibling_name || ''}>
                          {!a.has_sibling_at_mtk ? <span className="adm-muted">—</span> : (
                            <span className="sib-list">
                              {/* Bağlı olanlar: adı, sinfi və sətrinə keçid */}
                              {(a.siblings || []).map(sb => (
                                <span key={sb.id} className="enr-badge green sib-link clickable"
                                  role="button" tabIndex={0}
                                  onClick={e => { e.stopPropagation(); jumpToStudent(sb.id); }}
                                  onKeyDown={e => { if (e.key === 'Enter') { e.stopPropagation(); jumpToStudent(sb.id); } }}
                                  title={sb.name + (sb.class_name || sb.grade ? ' — ' + (sb.class_name || sb.grade) : '') +
                                         ' · ' + t('enrollment.jumpToSibling')}>
                                  {sb.name}
                                  {(sb.class_name || sb.grade) && (
                                    <b className="sib-cls">{sb.class_name || sb.grade}</b>
                                  )}
                                </span>
                              ))}
                              {/* Bağlanmayan adlar: yalnız mətn */}
                              {unlinkedSiblings(a).map((nm, ix) => (
                                <span key={'u' + ix} className="enr-badge green">{nm}</span>
                              ))}
                              {!(a.siblings || []).length && !unlinkedSiblings(a).length && (
                                <span className="enr-badge green">{t('admission.answerYes')}</span>
                              )}
                            </span>
                          )}
                        </td>
                        <td className="adm-prevschool" title={a.previous_school_name || ''}>
                          {PREV_SCHOOL_LABEL[a.previous_school_type] || '—'}
                          {a.previous_school_name && (
                            <div className="adm-sub">{a.previous_school_name}</div>
                          )}
                        </td>
                        <td style={{textAlign:'center'}} onClick={e => e.stopPropagation()}>
                          {/* Şəxsiyyət vəsiqəsi şəkilləri — serverdə uploads/admission/ içindədir */}
                          {admDocs(a).length ? (
                            <button className="adm-doc-btn" title={t('enrollment.admDocOpen')}
                              onClick={() => setDocView(admDocs(a))}>
                              🪪{admDocs(a).length > 1 && <b>{admDocs(a).length}</b>}
                            </button>
                          ) : <span className="adm-muted" title={t('enrollment.admDocNone')}>—</span>}
                        </td>
                        <td style={{textAlign:'center'}}>
                          <span className="enr-badge" style={{background:st.bg, color:st.color}}>{st.l}</span>
                        </td>
                        <td style={{textAlign:'center'}}>
                          {a.child_status && a.child_status !== 'normal' && (
                            <span className={'enr-badge ' + (a.child_status === 'shahid' ? 'red' : a.child_status === 'staff' ? 'purple' : 'blue')}
                              title={a.staff_parent_name || ''}>
                              {CHILD_STATUS_LABEL[a.child_status]}
                            </span>
                          )}
                          {/* Heyət övladının valideyni — sistemdəki əməkdaşa
                              bağlıdırsa adı kliklənir və kartı açılır */}
                          {a.child_status === 'staff' && a.staff_parent_name && (
                            a.staff_parent_user_id ? (
                              <button type="button" className="adm-staff-link"
                                title={t('enrollment.staffCardHint')}
                                onClick={e => { e.stopPropagation(); setStaffCardId(a.staff_parent_user_id); }}>
                                {a.staff_parent_name}
                              </button>
                            ) : <div className="adm-sub">{a.staff_parent_name}</div>
                          )}
                        </td>
                        <td style={{textAlign:'center'}} onClick={e => e.stopPropagation()}>
                          {/* Redaktə və qərar düymələri BİR sıradadır —
                              əvvəl redaktə ayrı sətirdə qalıb üst-üstə düşürdü */}
                          <div className="adm-actions">
                            {a.status === 'pending' && (
                              <>
                              <button className="enr-sign-btn" onClick={() => {                                if (window.confirm(a.student_first_name + ' ' + a.student_last_name + ' — ' + t('enrollment.confirmAccept')))
                                  updateAdmStatus(a.id, 'accepted');
                              }}>{t('enrollment.btnAccept')}</button>
                              <button className="adm-reject-btn" onClick={() => {
                                const note = prompt(t('enrollment.rejectReasonPrompt'));
                                if (note !== null) updateAdmStatus(a.id, 'rejected', note);
                              }}>{t('enrollment.btnReject')}</button>
                              </>
                            )}
                            {/* Redaktə qərar düymələrindən ayırıcı ilə ayrılır */}
                            {a.status === 'pending' && <span className="adm-act-sep" aria-hidden="true" />}
                            <button className="adm-edit-btn" title={t('common.edit')}
                              onClick={() => openEdit(a)}>
                              <Icon name="edit" size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      </div>{/* /enr-panel */}

      {/* MÜRACİƏTİN REDAKTƏSİ — qəbul formasının özü işlədilir ki,
          yeni müraciətdəki bütün sahələr burada da olsun. */}
      {newAdm && (
        <div className="adm-edit-overlay" onClick={() => setNewAdm(false)}>
          <div className="adm-edit-sheet" onClick={e => e.stopPropagation()}>
            <div className="adm-edit-top">
              <h3>{t('enrollment.btnNewAdmission')}</h3>
              <button onClick={() => setNewAdm(false)} aria-label={t('common.cancel')}>
                <Icon name="close" size={17} />
              </button>
            </div>
            <AdmissionForm
              onSaved={() => { setNewAdm(false); loadAdmissions(); }}
              onBack={() => setNewAdm(false)}
            />
          </div>
        </div>
      )}

      {editAdm && (
        <div className="adm-edit-overlay" onClick={() => setEditAdm(null)}>
          <div className="adm-edit-sheet" onClick={e => e.stopPropagation()}>
            <div className="adm-edit-top">
              <div>
                <h3>{t('admission.editTitle')}</h3>
                <span className="adm-edit-num">{editAdm.request_number}</span>
              </div>
              <button onClick={() => setEditAdm(null)} aria-label={t('common.cancel')}>
                <Icon name="close" size={17} />
              </button>
            </div>
            <AdmissionForm
              editData={editAdm}
              onSaved={() => { setEditAdm(null); loadAdmissions(); }}
              onBack={() => setEditAdm(null)}
            />
          </div>
        </div>
      )}

      {/* SIGN MODAL - sinif seçimi */}
      {signModal && (
        <div className="sign-modal-overlay" onClick={() => setSignModal(null)}>
          <div className="sign-modal" onClick={e => e.stopPropagation()}>
            <div className="sign-modal-hdr">
              <h3>Sinif Seçimi</h3>
              <button onClick={() => setSignModal(null)}><Icon name="close" size={16} /></button>
            </div>
            <p className="sign-modal-desc">
              <b>{signModal.student_first_name || signModal.student_name}</b> üçün sinif indeksini seçin
            </p>
            <div className="sign-grade-grid">
              {gradeOptions.map(g => (
                <label key={g.grade}
                  className={'sign-grade-opt' + (signGrade === g.grade ? ' sel' : '') + (g.isFull ? ' full' : '')}>
                  <input type="radio" style={{display:'none'}} disabled={g.isFull}
                    checked={signGrade === g.grade} onChange={() => !g.isFull && setSignGrade(g.grade)} />
                  <span className="sign-grade-name">{g.grade}</span>
                  <span className="sign-grade-count">{g.current}/{g.capacity}</span>
                  {g.isFull && <span className="sign-grade-full">DOLU</span>}
                </label>
              ))}
            </div>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:12}}>
              <button className="nrf-btn-cancel" onClick={() => setSignModal(null)} style={{padding:'8px 16px',border:'none',borderRadius:8,cursor:'pointer',background:'#f3f4f6',color:'#374151'}}>Ləğv et</button>
              <button className="enr-sign-btn" onClick={confirmSign} disabled={!signGrade}
                style={{padding:'8px 20px',fontSize:'0.88rem',opacity:signGrade?1:0.5}}>İmzala</button>
            </div>
          </div>
        </div>
      )}

      {/* Şəxsiyyət vəsiqəsi baxış pəncərəsi */}
      {docView && <IdPhotoViewer files={docView} onClose={() => setDocView(null)} />}

      {/* Əməkdaş kartı */}
      {staffCardId && <StaffCard userId={staffCardId} onClose={() => setStaffCardId(null)} t={t} />}
    </div>
  );
}
